import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Button } from "../react-general-components/Button";
import { useShopifyProduct } from "../react-hooks/shopify";
import { ProductMoreColorsContent } from "../react-product-more-colors/components/ProductMoreColors";
import { ProductPrice } from "../react-product/components/ProductPrice";
import { useLocaleContext } from "../react-providers/locales";
import { NormalisedProduct } from "../types";
import { ProductMediaItems } from "./components/ProductMediaItems";
import { ProductSizeSelect } from "./components/ProductSizeSelect";

type Props = {
  product: NormalisedProduct;
  toggleOpen?: () => void;
  productMediaSliderDesktop?: boolean;
  columnsDesktop?: string | undefined;
  columnsMobile?: string | undefined;
  smallButtons?: boolean;
  scrollbarMobile?: string | undefined;
};

export const ProductDetails: React.FC<Props> = ({
  product,
  toggleOpen,
  productMediaSliderDesktop = false,
  columnsDesktop = "2",
  columnsMobile = "1",
  smallButtons = false,
  scrollbarMobile = "false",
}) => {
  const { error, handleAddToCart, handleChangeVariant, selectedVariantId, title } = useShopifyProduct({ product });
  const { product: productLocale } = useLocaleContext();
  const [hasSizeGuide, setHasSizeGuide] = useState(false);

  const addToCart = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    const success = await handleAddToCart(e);
    if (success) {
      toggleOpen?.();
    }
  }, [handleAddToCart]);

  const isComingSoon = useMemo(() => product.tags.find(tag => tag.toLowerCase().includes("coming")), [product?.tags])

  const selectedVariant = useMemo(() => product?.variants?.find(v => v?.id?.toString() === selectedVariantId), [product, selectedVariantId])

  useEffect(() => {
    setHasSizeGuide(!!document.querySelector("[data-sizeguide]"));
  }, []);

  return (
    /* data-* attributes not used in solution. Requested by analyzify */
    <div className="product quick-add" data-variant={selectedVariantId} data-handle={product?.handle} data-title={product?.title}>
      <item-carousel
        columns-desktop={columnsDesktop}
        columns-mobile={columnsMobile}
        disabled-mobile="false"
        disabled-desktop={`${!productMediaSliderDesktop}`}
        small-btns={smallButtons}
        scrollbar-mobile={scrollbarMobile}
      >
        <div className="product-grid item-carousel columns-desktop__2 columns-mobile__1">
          <ProductMediaItems product={product} />
        </div>
      </item-carousel>

      <div className="page-width">
        <div className="product__title">
          <h2 className="h3 bold mb-2">{title}</h2>
        </div>

        <ProductPrice product={product} variant="product-card" selectedVariant={selectedVariant} />

        <ProductMoreColorsContent product={product} />

        <form method="post" action="/cart/add" onSubmit={addToCart} className="product__form">
          <input name="id" value={selectedVariantId} type="hidden" />

          <div className="product__flex">
            <h2 className="text-lg bold">{productLocale?.select_size || "Select Size"}</h2>
            {hasSizeGuide && <Button variant="text" ariaLabel={productLocale?.size_guide?.size_guide || "Size Guide"} label={productLocale?.size_guide?.size_guide || "Size Guide"} className="product__auto-h" data-opensizeguide />}
          </div>

          <ProductSizeSelect
            variants={product.variants}
            handleChangeVariant={handleChangeVariant}
            defaultTitle={productLocale?.select_size}
          />

          {error && <small className="form__message text-brand-error">{error}</small>}

          <div className="product__add-to-cart flex">
            { isComingSoon
              ? <Button type="button" label="Coming Soon" className="add-to-cart capitalize font-medium text-lg w-ful" href={`/products/${product.handle}`} />
              : <Button type="submit" label={productLocale?.add_to_cart || "Add to bag"} className="add-to-cart capitalize font-medium text-lg w-full" />
            }
            {/* <Button type="submit" label={productLocale?.add_to_cart || "Add to bag"} className="add-to-cart capitalize font-medium text-lg w-full" /> */}
            <Button
              href={`/products/${product.handle}`}
              variant="text"
              label={productLocale?.view_details || "View Details"}
              className="product__auto-h details w-3"
            />
          </div>
        </form>
      </div>
    </div>
  );
};
